import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import {
  ShoppingBag,
  Search,
  ArrowRight,
  Plus,
  Minus,
  Sparkles,
  UtensilsCrossed,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import { useCart } from "../App";
import { orderApi } from "../lib/orderApi";
import { staticMenuItems, menuCategories } from "../data/menu";

export default function OrderMenu() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const { tableId, tableName, setTable, cart, addToCart, updateQuantity, cartCount, cartTotal } =
    useCart();

  const tableParam = searchParams.get("table") || tableId;

  const [products, setProducts] = useState(staticMenuItems);
  const [categories, setCategories] = useState(menuCategories);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [isLiveApi, setIsLiveApi] = useState(false);

  // Set table from query param if available
  useEffect(() => {
    if (tableParam && tableParam !== "null") {
      setTable(tableParam, `Table ${tableParam}`);
    } else if (!tableId) {
      navigate("/select-table");
    }
  }, [tableParam, tableId, navigate, setTable]);

  // Attempt to fetch fresh menu from backend API, fall back to staticMenuItems on error/timeout
  useEffect(() => {
    const loadLiveMenu = async () => {
      setLoading(true);
      try {
        const [catData, prodData] = await Promise.all([
          orderApi.getCategories(),
          orderApi.getProducts(),
        ]);

        if (Array.isArray(prodData) && prodData.length > 0) {
          // Normalize backend products
          const formatted = prodData.map((p) => ({
            id: p.id,
            name: p.name,
            price: Number(p.price) || 0,
            description: p.description || "",
            image: p.image_url || "/menu1.jpeg",
            category: p.category_id || "ice-cream",
            categoryName:
              catData?.find((c) => c.id === p.category_id)?.name || "Ice Cream",
            is_available: p.is_available,
          }));

          setProducts(formatted);
          setIsLiveApi(true);

          if (Array.isArray(catData) && catData.length > 0) {
            setCategories([
              { id: "all", name: "All Items" },
              ...catData.map((c) => ({ id: c.id, name: c.name })),
            ]);
          }
        }
      } catch (err) {
        console.log("Using local offline menu data for table ordering.");
      } finally {
        setLoading(false);
      }
    };

    loadLiveMenu();
  }, []);

  // Filter products by category and search
  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      const matchCategory =
        selectedCategory === "all" ||
        p.category === selectedCategory ||
        p.category_id === selectedCategory;

      const matchSearch =
        !searchQuery ||
        p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.description?.toLowerCase().includes(searchQuery.toLowerCase());

      const available = p.is_available !== 0 && p.is_available !== false;

      return matchCategory && matchSearch && available;
    });
  }, [products, selectedCategory, searchQuery]);

  const getItemQty = (id) => {
    const found = cart.find((item) => String(item.id) === String(id));
    return found ? found.quantity : 0;
  };

  return (
    <div className="min-h-screen bg-[#FFFDF9] pb-28 max-w-2xl mx-auto">
      {/* 🍨 STICKY TOP TABLE BAR */}
      <div className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-stone-200 px-4 py-3 shadow-xs">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <Link to="/">
              <img
                src="/logo.png"
                alt="HimCream"
                className="h-8 w-auto"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            </Link>
            <div>
              <div className="flex items-center gap-1.5">
                <span className="font-extrabold text-sm text-[#2C3E50]">
                  {tableName || (tableId ? `Table ${tableId}` : "Select Table")}
                </span>
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              </div>
              <p className="text-[10px] text-stone-400">Ordering Menu</p>
            </div>
          </div>

          <Link
            to="/select-table"
            className="text-xs font-bold text-sky-600 hover:text-sky-700 bg-sky-50 hover:bg-sky-100 px-3 py-1.5 rounded-full transition"
          >
            Change Table
          </Link>
        </div>

        {/* SEARCH BAR */}
        <div className="relative mt-3">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <input
            type="text"
            placeholder="Search ice creams, shakes, faluda..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-xs sm:text-sm bg-stone-50 border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500"
          />
        </div>

        {/* CATEGORY CHIPS */}
        <div className="flex gap-2 overflow-x-auto pt-3 pb-1 scrollbar-none">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setSelectedCategory(cat.id)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition ${
                selectedCategory === cat.id
                  ? "bg-[#2C3E50] text-white shadow-xs"
                  : "bg-white text-stone-600 border border-stone-200 hover:bg-stone-50"
              }`}
            >
              {cat.name}
            </button>
          ))}
        </div>
      </div>

      {/* 🍨 PRODUCT LIST */}
      <div className="p-4 space-y-3">
        <div className="flex items-center justify-between text-xs text-stone-500 font-semibold px-1">
          <span>{filteredProducts.length} Items Available</span>
          <span className="text-[11px] text-stone-400">100% Real Fruit</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
          {filteredProducts.map((item) => {
            const qty = getItemQty(item.id);

            return (
              <div
                key={item.id}
                className="bg-white p-3.5 rounded-2xl border border-stone-200/80 shadow-xs flex gap-3.5 items-center justify-between group"
              >
                {/* Image */}
                <div className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-xl overflow-hidden bg-stone-100 shrink-0">
                  <img
                    src={item.image || item.image_url || "/menu1.jpeg"}
                    alt={item.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    onError={(e) => {
                      e.target.src = "/menu1.jpeg";
                    }}
                  />
                  {item.badge && (
                    <span className="absolute bottom-1 left-1 bg-black/70 text-white text-[8px] font-bold px-1.5 py-0.5 rounded">
                      {item.badge}
                    </span>
                  )}
                </div>

                {/* Details */}
                <div className="flex-1 min-w-0 pr-2">
                  <h3 className="font-bold text-sm text-[#2C3E50] truncate">
                    {item.name}
                  </h3>
                  {item.description && (
                    <p className="text-xs text-stone-500 line-clamp-2 mt-0.5 leading-relaxed">
                      {item.description}
                    </p>
                  )}
                  <p className="font-extrabold text-sm text-sky-600 mt-1">
                    ₹{Number(item.price).toFixed(2)}
                  </p>
                </div>

                {/* ADD / QTY CONTROL */}
                <div className="shrink-0">
                  {qty === 0 ? (
                    <button
                      onClick={() => addToCart(item)}
                      className="bg-sky-500 hover:bg-sky-600 active:scale-95 text-white font-bold text-xs px-3.5 py-2 rounded-xl shadow-xs flex items-center gap-1 transition"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      <span>Add</span>
                    </button>
                  ) : (
                    <div className="flex items-center gap-1 bg-stone-100 border border-stone-200 rounded-xl p-1">
                      <button
                        onClick={() => updateQuantity(item.id, qty - 1)}
                        className="w-6 h-6 rounded-lg bg-white text-stone-700 font-bold text-xs flex items-center justify-center shadow-xs active:scale-90"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="font-extrabold text-xs px-2 text-[#2C3E50]">
                        {qty}
                      </span>
                      <button
                        onClick={() => updateQuantity(item.id, qty + 1)}
                        className="w-6 h-6 rounded-lg bg-sky-500 text-white font-bold text-xs flex items-center justify-center shadow-xs active:scale-90"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 🛒 STICKY BOTTOM CART BAR */}
      {cartCount > 0 && (
        <div className="fixed bottom-4 left-4 right-4 max-w-md mx-auto z-40">
          <button
            onClick={() => navigate("/cart")}
            className="w-full bg-gradient-to-r from-sky-500 to-sky-600 hover:from-sky-600 hover:to-sky-700 text-white p-4 rounded-2xl shadow-xl shadow-sky-500/30 flex items-center justify-between font-bold text-sm hover:scale-[1.02] active:scale-98 transition-all"
          >
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center text-xs">
                {cartCount}
              </div>
              <div className="text-left">
                <p className="text-xs text-sky-100 font-normal">
                  {tableName || `Table ${tableId}`}
                </p>
                <p className="font-extrabold text-base">₹{cartTotal.toFixed(2)}</p>
              </div>
            </div>

            <div className="flex items-center gap-1.5 bg-white/20 px-3.5 py-1.5 rounded-xl">
              <span>View Cart</span>
              <ArrowRight className="w-4 h-4" />
            </div>
          </button>
        </div>
      )}
    </div>
  );
}
